import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import EmployeePicker from "../../../components/ui/EmployeePicker";
import {
  useCancelEvaluation,
  useChangeEvaluator,
  useResolveContest,
} from "../../../api/queries/performanceQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { getApiErrorStatus } from "../../../utils/apiError";
import { employeeName } from "../../../utils/text";
import { errorList, statusLabel } from "./evaluationUtils";

/**
 * 409 (changed by someone else) → toast, close (lists refetch in the mutation).
 * 403 → toast, close. Anything else → shown inside the dialog.
 */
const useErrorHandler = (onClose, setErrors) =>
  useCallback(
    (err) => {
      const status = getApiErrorStatus(err);
      const list = errorList(err, "Something went wrong");
      if (status === 409) {
        toast.info(list.join(" "));
        onClose();
      } else if (status === 403) {
        toast.error(list.join(" "));
        onClose();
      } else {
        setErrors(list);
      }
    },
    [onClose, setErrors],
  );

const Errors = ({ errors }) =>
  errors.length ? (
    <div className="alert alert-danger fs-085 py-2" role="alert">
      {errors.length === 1 ? (
        errors[0]
      ) : (
        <ul className="mb-0 ps-3">
          {errors.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  ) : null;

/** Who / which form / which period, at the top of each dialog. */
const Summary = ({ evaluation: ev }) => (
  <div className="eval-summary">
    <div className="fw-semibold text-navy">
      {employeeName(ev.employee)}{" "}
      <span className="text-slate-500 fw-normal fs-080">
        {ev.employee?.employeeId}
      </span>
    </div>
    <div className="fs-080 text-slate-600">
      {ev.template?.name} · {ev.periodLabel} · {statusLabel(ev)}
      {ev.totalScore !== null &&
        ev.totalScore !== undefined &&
        ` · ${ev.totalScore}${ev.ratingBand ? ` (${ev.ratingBand})` : ""}`}
    </div>
  </div>
);

const RemarksField = ({
  id,
  label,
  value,
  onChange,
  required,
  max,
  placeholder,
}) => (
  <div>
    <label htmlFor={id} className="profile-label">
      {label}{" "}
      {required ? (
        <span className="text-danger">*</span>
      ) : (
        <span className="text-slate-400 fw-normal">(optional)</span>
      )}
    </label>
    <textarea
      id={id}
      className="form-control"
      rows={3}
      maxLength={max}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus
    />
    <div className="text-end text-slate-400 fs-070 mt-1">
      {value.length}/{max}
    </div>
  </div>
);

const Footer = ({ onClose, busy, onSubmit, disabled, label, variant }) => (
  <>
    <button
      type="button"
      className="btn btn-outline-secondary profile-btn"
      onClick={onClose}
      disabled={busy}
    >
      Close
    </button>
    <button
      type="button"
      className={`btn btn-${variant} profile-btn`}
      onClick={onSubmit}
      disabled={busy || disabled}
    >
      {busy && (
        <span
          className="spinner-border spinner-border-sm"
          aria-hidden="true"
        ></span>
      )}
      {label}
    </button>
  </>
);

/** Pick a new evaluator (not the employee, not the current evaluator). */
export const ReassignDialog = ({ evaluation, onClose }) => {
  const user = useCurrentUser();
  const [evaluator, setEvaluator] = useState(null);
  const [errors, setErrors] = useState([]);
  const change = useChangeEvaluator();
  const onError = useErrorHandler(onClose, setErrors);

  const submit = () => {
    setErrors([]);
    change.mutate(
      { id: evaluation.id, evaluatorEmployeeId: evaluator.id },
      {
        onSuccess: () => {
          toast.success(`Evaluator changed to ${employeeName(evaluator)}.`);
          onClose();
        },
        onError,
      },
    );
  };

  return (
    <Dialog
      title="Reassign evaluator"
      onClose={onClose}
      busy={change.isPending}
      footer={
        <Footer
          onClose={onClose}
          busy={change.isPending}
          onSubmit={submit}
          disabled={!evaluator}
          label="Reassign"
          variant="primary"
        />
      }
    >
      <Errors errors={errors} />
      <Summary evaluation={evaluation} />
      <p className="fs-085 mb-2">
        Current evaluator: <strong>{employeeName(evaluation.evaluator)}</strong>
        . Ratings already saved stay as they are.
      </p>
      <label htmlFor="reassign-evaluator" className="profile-label">
        New evaluator <span className="text-danger">*</span>
      </label>
      <EmployeePicker
        id="reassign-evaluator"
        companyId={user.companyId}
        value={evaluator}
        onChange={setEvaluator}
        excludeId={[evaluation.employeeId, evaluation.evaluatorEmployeeId]}
        autoFocus
      />
    </Dialog>
  );
};

/** Cancel with a required reason. */
export const CancelEvaluationDialog = ({ evaluation, onClose }) => {
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState([]);
  const cancel = useCancelEvaluation();
  const onError = useErrorHandler(onClose, setErrors);

  const submit = () => {
    setErrors([]);
    cancel.mutate(
      { id: evaluation.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success("Evaluation cancelled.");
          onClose();
        },
        onError,
      },
    );
  };

  return (
    <Dialog
      title="Cancel evaluation?"
      onClose={onClose}
      busy={cancel.isPending}
      footer={
        <Footer
          onClose={onClose}
          busy={cancel.isPending}
          onSubmit={submit}
          disabled={!reason.trim()}
          label="Cancel evaluation"
          variant="danger"
        />
      }
    >
      <Errors errors={errors} />
      <Summary evaluation={evaluation} />
      <p className="fs-080 text-slate-600">
        This can't be undone. Ratings stay in the history.
      </p>
      <RemarksField
        id="cancel-reason"
        label="Reason"
        required
        max={500}
        value={reason}
        onChange={setReason}
        placeholder="e.g. Employee resigned before evaluation"
      />
    </Dialog>
  );
};

/**
 * HR resolves an employee's contest: UPHOLD keeps the result (→ Completed);
 * REEVALUATE sends it back to the evaluator; when re-submitted it goes to the employee again.
 */
export const ResolveContestDialog = ({ evaluation, onClose }) => {
  const [action, setAction] = useState("UPHOLD");
  const [remarks, setRemarks] = useState("");
  const [errors, setErrors] = useState([]);
  const resolve = useResolveContest();
  const onError = useErrorHandler(onClose, setErrors);

  const submit = () => {
    setErrors([]);
    resolve.mutate(
      { id: evaluation.id, action, remarks: remarks.trim() },
      {
        onSuccess: () => {
          toast.success(
            action === "UPHOLD"
              ? "Result upheld: evaluation completed."
              : "Sent back to the evaluator for re-evaluation.",
          );
          onClose();
        },
        onError,
      },
    );
  };

  return (
    <Dialog
      title="Resolve contest"
      onClose={onClose}
      busy={resolve.isPending}
      footer={
        <Footer
          onClose={onClose}
          busy={resolve.isPending}
          onSubmit={submit}
          disabled={!remarks.trim()}
          label={action === "UPHOLD" ? "Uphold result" : "Re-evaluate"}
          variant={action === "UPHOLD" ? "primary" : "warning"}
        />
      }
    >
      <Errors errors={errors} />
      <Summary evaluation={evaluation} />
      {evaluation.employeeComment && (
        <div className="approval-note approval-note--rejected mb-3">
          <div className="approval-meta-label">Employee's reason</div>
          {evaluation.employeeComment}
        </div>
      )}
      <div
        className="d-flex flex-column gap-2 mb-3"
        role="radiogroup"
        aria-label="Decision"
      >
        {[
          [
            "UPHOLD",
            "Uphold the result",
            "The evaluation stays as it is and is completed.",
          ],
          [
            "REEVALUATE",
            "Re-evaluate",
            "Back to the evaluator; when re-submitted it goes to the employee again.",
          ],
        ].map(([value, label, hint]) => (
          <label
            key={value}
            className={`contest-option ${action === value ? "active" : ""}`}
          >
            <input
              type="radio"
              name="contest-action"
              value={value}
              checked={action === value}
              onChange={() => setAction(value)}
              className="form-check-input me-2"
            />
            <span>
              <strong className="fs-085">{label}</strong>
              <span className="d-block fs-075 text-slate-500">{hint}</span>
            </span>
          </label>
        ))}
      </div>
      <RemarksField
        id="contest-remarks"
        label="Remarks"
        required
        max={1000}
        value={remarks}
        onChange={setRemarks}
        placeholder="Explain the decision to the employee"
      />
    </Dialog>
  );
};

const DIALOGS = {
  reassign: ReassignDialog,
  cancel: CancelEvaluationDialog,
  resolve: ResolveContestDialog,
};

/** open(type, evaluation) with type reassign | cancel | resolve; render `dialog`. */
export const useEvaluationDialogs = () => {
  const [active, setActive] = useState(null);
  const close = useCallback(() => setActive(null), []);
  const Component = active ? DIALOGS[active.type] : null;
  return {
    open: (type, evaluation) => setActive({ type, evaluation }),
    dialog: Component ? (
      <Component evaluation={active.evaluation} onClose={close} />
    ) : null,
  };
};
